/**
 * FormStepIndicator (PADD-3866)
 *
 * A read-only, numbered page/step progress indicator for multi-page forms.
 * Matches the ticket "States" spec: numbered circles joined by horizontal
 * connectors, with four node states:
 *
 *   - completed : blue outline, pale-blue fill, blue check icon
 *   - active    : solid blue fill, white number
 *   - upcoming  : grey outline, white fill, grey number
 *   - error     : red outline, white fill, red "!" (current page failed validation)
 *
 * The connector leading INTO a node is blue once that node is reached
 * (i.e. the previous step is completed), otherwise grey.
 *
 * Presentational only — it does not drive navigation. The host renders its own
 * Previous/Next buttons and decides whether to show the indicator at all (e.g.
 * it must be hidden when branching is active, per PADD-3866).
 *
 * Props:
 * - stepCount (number)   — total number of steps. Ignored when `steps` is given.
 * - steps (array)        — optional array (its length sets the number of steps).
 *                          Only the length is used; labels are intentionally not
 *                          rendered (the States spec is numbers-only).
 * - currentStep (number) — 0-based index of the active step. Steps before it are
 *                          "completed"; steps after it are "upcoming".
 * - errorStep (number)   — optional 0-based index of a step to show in the error
 *                          state (typically the current step when its required
 *                          fields failed validation on a Next attempt). Null/undefined
 *                          for no error.
 */
import React from "react";

import "../styles/FormStepIndicator.scss";

const CheckIcon = () => (
    <svg
        className="form-step-indicator__check"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
    >
        <path
            d="M6 12.5 L10 16.5 L18 7.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const FormStepIndicator = ({ stepCount, steps, currentStep = 0, errorStep = null }) => {
    const total = Array.isArray(steps) ? steps.length : (stepCount || 0);

    // Nothing to show for single-page (or empty) forms.
    if (!total || total <= 1) return null;

    const getState = (index) => {
        if (errorStep !== null && errorStep !== undefined && index === errorStep) {
            return "error";
        }
        if (index < currentStep) return "completed";
        if (index === currentStep) return "active";
        return "upcoming";
    };

    return (
        <div
            className="form-step-indicator"
            role="group"
            aria-label={`Step ${currentStep + 1} of ${total}`}
        >
            {Array.from({ length: total }).map((_, index) => {
                const state = getState(index);
                const isCompleted = state === "completed";
                // The connector sitting BEFORE this node is "filled" once the
                // previous step has been completed (i.e. we've reached this node).
                const connectorFilled = index > 0 && index <= currentStep;

                return (
                    <React.Fragment key={index}>
                        {index > 0 && (
                            <span
                                className={
                                    "form-step-indicator__connector" +
                                    (connectorFilled ? " is-filled" : "")
                                }
                                aria-hidden="true"
                            />
                        )}
                        <span
                            className={`form-step-indicator__step is-${state}`}
                            aria-current={state === "active" ? "step" : undefined}
                        >
                            {isCompleted ? (
                                <CheckIcon />
                            ) : state === "error" ? (
                                <span className="form-step-indicator__mark" aria-hidden="true">!</span>
                            ) : (
                                <span className="form-step-indicator__number">{index + 1}</span>
                            )}
                        </span>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default FormStepIndicator;
